import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Box, Card, CardHeader, IconButton, Typography } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { forEach } from 'lodash';
import { useSelector } from 'react-redux';
import { fData } from '../../../utils/formatNumber';
import { useChart } from '../../../components/chart';
import axiosInstance from '../../../utils/axiosInstance';

AppSensorStorage.propTypes = {
  title: PropTypes.string,
  subheader: PropTypes.string,
  host: PropTypes.string.isRequired,
};

export default function AppSensorStorage({ title, subheader, host, ...other }) {
  const [chartData, setChartData] = useState([]);
  const [page, setPage] = useState(0);
  const itemsPerPage = 5;

  const totalPages = Math.ceil(chartData.length / itemsPerPage);
  const chartLabels = chartData.map((i) => i.label);
  const chartSeries = chartData.map((i) => i.value);
  const sensorList = useSelector((state) => state.app.sensorList);

  const chartOptions = useChart({
    tooltip: {
      marker: { show: false },
      y: {
        formatter: (seriesName) => fData(Number(seriesName) * 1024 * 1024),
        title: {
          formatter: () => '',
        },
      },
    },
    plotOptions: {
      bar: { horizontal: true, barHeight: '28%', borderRadius: 2 },
    },
    xaxis: {
      categories: chartLabels.slice(page * itemsPerPage, (page + 1) * itemsPerPage),
    },
  });

  useEffect(() => {
    const parseRecordApiResponse = (obj) => {
      const parsedData = [];
      forEach(obj, (nestedObj, key) => {
        if (key !== 'total') {
          forEach(sensorList, (sensor) => {
            if (sensor.sensorId === key && sensor.host === host) {
              const temp = {
                label: sensor.name,
                value: nestedObj.sizeInMb,
              };
              parsedData.push(temp);
            }
          });
        }
      });
      setChartData(parsedData);
    };

    axiosInstance
      .get(`${window.location.protocol}//${host}/api/v1/storage/size`)
      .then((response) => {
        if (response.data) {
          parseRecordApiResponse(response.data);
        }
      })
      .catch((error) => {
        console.log('Failed to get VST record size', error);
      });
  }, [host, sensorList]);

  const handleNext = () => {
    if (page + 1 < totalPages) {
      setPage((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (page > 0) {
      setPage((prev) => prev - 1);
    }
  };

  return (
    <Card {...other}>
      <CardHeader title={title} subheader={subheader} />

      <Box sx={{ mx: 3 }} dir="ltr">
        <ReactApexChart
          type="bar"
          series={[{ data: chartSeries.slice(page * itemsPerPage, (page + 1) * itemsPerPage) }]}
          options={chartOptions}
          height={364}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 2, mr: 2, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {`${page * itemsPerPage + 1}–${Math.min((page + 1) * itemsPerPage, chartData.length)} of ${chartData.length}`}
        </Typography>
        <IconButton onClick={handlePrev} disabled={page === 0}>
          <KeyboardArrowLeft />
        </IconButton>
        <IconButton onClick={handleNext} disabled={page + 1 >= totalPages}>
          <KeyboardArrowRight />
        </IconButton>
      </Box>
    </Card>
  );
}